// GlobalComponents/UpdateStatusPopup/UpdateStatusPopup.jsx
import React, { useMemo, useState } from 'react';
import Classes from './styles/style.module.css';
import { motion } from 'framer-motion';
import DropDown from './StatusDropDown/DropDown';
import { usePreventScrolling } from '../../../Hooks/usePreventScrolling';
import { CloseIcon } from '../../../utils/Icons';
import backend from '../../../api';

const UI_TO_DB = {
  "Completed": "completed",
  "Upcoming": "upcoming",
  "Cancelled": "cancelled",
  "On the way": "on_the_way",
  "Delayed": "delayed",
};
const DB_TO_UI = Object.fromEntries(Object.entries(UI_TO_DB).map(([k, v]) => [v, k]));

export default function UpdateStatusPopup({ closePopup, booking, initialStatus = "Upcoming", onSaved }) {
  usePreventScrolling();

  const [statusLabel, setStatusLabel] = useState(initialStatus);
  const [message, setMessage] = useState("");
  const [eta, setEta] = useState(""); // "YYYY-MM-DDTHH:mm" (use datetime-local for better UX)
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const statusOptions = useMemo(
    () => ["Completed", "Upcoming", "Cancelled", "On the way", "Delayed"],
    []
  );

  const handleSubmit = async () => {
    console.log("submit status:", booking);
    if (!booking?._id) return;
    setSaving(true);
    setErr("");
    try {
      // Build ISO string if eta is provided
      // If your UI uses <input type="date"> change the parsing accordingly
      const etaISO = eta ? new Date(eta).toISOString() : null;

      const { data } = await backend.put(`/api/bookings/${booking._id}/status`, {
        status: statusLabel,     // can be UI label; backend maps to db value
        message,
        etaAt: etaISO,
      });

      setSaving(false)

      if (data?.ok) {
        onSaved?.(statusLabel);
        setSaving(false)
      } else {
        setErr(data?.error || "Failed to update status");
      }
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
      className={Classes.popup} onClick={closePopup}
    >
      <motion.div
        initial={{ y: 7 }} animate={{ y: 0 }} exit={{ y: 7 }} transition={{ duration: 0.25 }}
        className={Classes.card} onClick={(e) => e.stopPropagation()}
      >
        <div className={Classes.close} onClick={closePopup}><CloseIcon height='20px' fill='white' /></div>

        <div className={Classes.bgPopup}>
          <div />
          <h5 className={Classes.title}>Update Status</h5>
          <div className={Classes.curveCon} />
        </div>

        <div className={Classes.cardCon}>
          {/* Status */}
          <br/>
          <div className={Classes.flexColGroup}>
            <div className={Classes.flexCol1}>
              <h5 className={Classes.label}>Status</h5>
              <DropDown
                selectedValue={statusLabel}
                options={statusOptions}
                onChange={(val) => setStatusLabel(val)}
                isDropDown={true}
              />
            </div>
          </div>

          {/* Message */}
          <div className={Classes.flexColGroup}>
            <div className={Classes.flexCol1}>
              <div className='flexAlign10'>
                <h5 className={Classes.label}>Message to Client</h5>
                <div className={Classes.optionalText}><h5>Optional</h5></div>
              </div>
              <div className={Classes.textAreaCon}>
                <textarea rows={4} name="notes" value={message} onChange={(e) => setMessage(e.target.value)} />
              </div>
            </div>
          </div>

          {/* ETA */}
          <div className={Classes.flexColGroup}>
            <div className={Classes.flexCol1}>
              <div className='flexAlign10'>
                <h5 className={Classes.label}>Estimated Time of Arrival</h5>
                <div className={Classes.optionalText}><h5>Optional</h5></div>
              </div>
              <input
                type="datetime-local"                 // better than date-only
                className={Classes.inputField}
                value={eta}
                onChange={(e) => setEta(e.target.value)}
              />
            </div>
          </div>

          {err && <p style={{ color: 'crimson', marginTop: 8 }}>{err}</p>}

          <div className={Classes.flexAlign}>
            <button type="button" onClick={closePopup} className={Classes.btn1}><h5>Back</h5></button>
            <button type="button" onClick={handleSubmit} className={Classes.btn2} disabled={saving}>
              <h5>{saving ? "Saving..." : "Confirm"}</h5>
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
